// src/pages/products/ProductDetail.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Couleurs de la charte graphique
const COLORS = {
  primary: '#0B3C5D', // Bleu Sécurité
  secondary: '#1E88C8', // Bleu Tech
  accent: '#4FC3F7', // Bleu Clair
  darkText: '#2E2E2E', // Gris Foncé
  lightBg: '#F5F7FA', // Gris Clair
  white: '#FFFFFF',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  info: '#17a2b8'
};

const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [entreesProduit, setEntreesProduit] = useState([]);
  const [sortiesProduit, setSortiesProduit] = useState([]);
  const [loadingProduit, setLoadingProduit] = useState(true);
  const [loadingEntrees, setLoadingEntrees] = useState(false);
  const [loadingSorties, setLoadingSorties] = useState(false);
  const [typeProduits, setTypeProduits] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [error, setError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [activeTab, setActiveTab] = useState("entrees");

  // Fonction pour charger les types de produits
  const loadTypeProduits = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Token d'authentification manquant.");
        setLoadingTypes(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/stock/type_produit/list_type_produit.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data?.success && response.data?.data) {
        setTypeProduits(response.data.data.map(type => ({
          value: type.id.toString(),
          label: type.type
        })));
      }
    } catch (err) {
      console.error("Erreur chargement types produits:", err);
    } finally {
      setLoadingTypes(false);
    }
  };

  // Fonction pour charger les informations du produit
  const loadProduitDetail = async () => {
    setLoadingProduit(true);
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Token d'authentification manquant.");
        setLoadingProduit(false);
        return;
      }

      const url = `${API_BASE_URL}/stock/produits/list_produit.php`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data?.success) {
        const produits = response.data.data?.produits || [];
        const produit = produits.find(p => p.id == id);
        
        if (produit) {
          setSelectedProduit(produit);
        } else {
          setError("Produit introuvable.");
        }
      } else {
        setError(response.data?.message || "Erreur lors du chargement.");
      }
    } catch (err) {
      console.error("Erreur chargement produit:", err);
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError("Erreur lors du chargement du produit.");
      }
    } finally {
      setLoadingProduit(false);
    }
  };

  // Fonction pour charger les entrées
  const loadEntrees = async () => {
    if (!selectedProduit) return;
    
    setLoadingEntrees(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setLoadingEntrees(false);
        return;
      }
      
      const urlEntrees = `${API_BASE_URL}/stock/entrer_stock/detail_entrer.php?id_produit=${selectedProduit.id || id}`;
      const responseEntrees = await axios.get(urlEntrees, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (responseEntrees.data?.success) {
        setEntreesProduit(responseEntrees.data.entrees || []);
      }
    } catch (err) {
      console.error("Erreur chargement entrées:", err);
    } finally {
      setLoadingEntrees(false);
    }
  };

  // Fonction pour charger les sorties
  const loadSorties = async () => {
    if (!selectedProduit) return;
    
    setLoadingSorties(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setLoadingSorties(false);
        return;
      }
      
      const urlSorties = `${API_BASE_URL}/stock/sortir_stock/detail_sortie.php?id_produit=${selectedProduit.id || id}`;
      const responseSorties = await axios.get(urlSorties, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (responseSorties.data?.success) {
        setSortiesProduit(responseSorties.data.data?.sorties || []);
      }
    } catch (err) {
      console.error("Erreur chargement sorties:", err);
    } finally {
      setLoadingSorties(false);
    }
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadTypeProduits();
    loadProduitDetail();
  }, [id]);

  // Charger entrées et sorties quand le produit est chargé
  useEffect(() => {
    if (selectedProduit) {
      loadEntrees();
      loadSorties();
    }
  }, [selectedProduit]);

  // Fonction utilitaire pour le label du type de produit
  const typeProduitLabel = (typeValue) => {
    if (!typeValue) return "";
    const str = String(typeValue).trim();
    if (!str) return "";

    let found = typeProduits.find((opt) => opt.value === str);
    if (!found) {
      found = typeProduits.find((opt) => opt.label.toLowerCase() === str.toLowerCase());
    }
    if (!found && typeProduits.some(opt => opt.label === str)) {
      return str;
    }
    return found ? found.label : typeValue;
  };

  // Fonctions de formatage
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === '0000-00-00 00:00:00') return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === '0000-00-00 00:00:00') return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return dateString;
    }
  };

  // Fonction pour le label de destination
  const getDestinationLabel = (destination) => {
    if (!destination) return "N/A";
    
    if (typeof destination === 'string') {
      return destination;
    }
    
    switch (destination.type) {
      case 'agent':
        return `Agent: ${destination.nom} ${destination.prenom || ''}`;
      case 'site':
        return `Site: ${destination.nom}`;
      case 'controleur':
        return `Contrôleur: ${destination.nom} ${destination.prenom || ''}`;
      case 'self':
        const profileLabel = destination.profile_label || 
                           (destination.profile === 1 ? 'administrateur' : 
                            destination.profile === 3 ? 'gestionnaire' : 'utilisateur');
        return `${profileLabel}: ${destination.nom} ${destination.prenom || ''}`;
      default:
        if (destination.nom && destination.prenom) {
          return `${destination.nom} ${destination.prenom}`;
        } else if (destination.nom) {
          return destination.nom;
        }
        return "N/A";
    }
  };

  // Calculer les statistiques
  const stats = {
    totalEntrees: entreesProduit.reduce((sum, entree) => sum + (entree.quantite_entree || 0), 0),
    totalSorties: sortiesProduit.reduce((sum, sortie) => sum + (sortie.quantite_sortie || sortie.quantite_sortie_stock || 0), 0),
    mouvementsTotal: entreesProduit.length + sortiesProduit.length
  };

  // Fonctions pour gérer les mouvements de stock
  const handleEntrerStock = () => {
   navigate(`/entrer-stock?produit=${selectedProduit.id}`);
  };

  const handleSortirStock = () => {
    navigate(`/sortir-stock?produit=${selectedProduit.id}`);
  };

  // Afficher le chargement
  if (loadingProduit || loadingTypes) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/products" style={{ color: COLORS.secondary }}>
                  Produits
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>
                Chargement...
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2" style={{ color: COLORS.darkText }}>
              {loadingProduit ? "Chargement du produit..." : "Chargement des types de produits..."}
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  // Afficher l'erreur si produit non trouvé
  if (error || !selectedProduit) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: 600 }}>
            Gestion de stocks
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/products" style={{ color: COLORS.secondary }}>
                  Produits
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>
                Erreur
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: `1px solid ${COLORS.lightBg}`
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: `${COLORS.danger}15`,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${COLORS.danger}`
                }}>
                  <i className="bi bi-exclamation-triangle" style={{ fontSize: '2rem', color: COLORS.danger }}></i>
                </div>
              </div>
              <div className="alert" style={{
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                borderColor: COLORS.danger,
                color: COLORS.danger,
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`,
                display: 'inline-block',
                textAlign: 'left'
              }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error || "Produit introuvable"}
              </div>
              <div className="mt-4">
                <button
                  className="btn mt-3"
                  onClick={() => navigate("/products")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    fontWeight: 500,
                    padding: '0.5rem 2rem',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  Retour à la liste
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: 600 }}>
          Gestion de stocks
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/products" style={{ color: COLORS.secondary }}>
                Produits
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>
              Détail du produit
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {/* Messages */}
        {infoMsg && (
          <div className="alert mb-4" style={{
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            borderColor: COLORS.success,
            color: COLORS.success,
            borderRadius: '8px',
            borderLeft: `4px solid ${COLORS.success}`
          }}>
            <i className="bi bi-check-circle me-2"></i>
            {infoMsg}
          </div>
        )}

        {/* En-tête avec informations produit */}
        <div className="card mb-4" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="mb-3">
              <h4 className="card-title" style={{ color: COLORS.primary, fontWeight: 600, marginBottom: '0.25rem' }}>
                <i className="bi bi-box me-2" style={{ color: COLORS.secondary }}></i>
                {selectedProduit.libele}
              </h4>
              <p className="text-muted" style={{ marginBottom: 0 }}>
                <small>
                  <i className="bi bi-calendar me-1"></i>
                  Créé le: {formatDateShort(selectedProduit.created_at)}
                </small>
              </p>
            </div>

            {/* Cartes de statistiques */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card h-100" style={{ 
                  border: `1px solid ${COLORS.primary}`,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(11, 60, 93, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.primary}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-box-seam" style={{ fontSize: '1.5rem', color: COLORS.primary }}></i>
                      </div>
                      <div>
                          <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                            Stock actuel
                          </h6>
                          <h4 className="mb-0" style={{ color: COLORS.primary, fontWeight: 700 }}>
                            {selectedProduit.quantite_stock}
                          </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                  <div className="card h-100" style={{ 
                    border: `1px solid ${COLORS.info}`,
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(23, 162, 184, 0.1)'
                  }}>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '48px',
                          height: '48px',
                          backgroundColor: `${COLORS.info}15`,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px',
                          flexShrink: 0
                        }}>
                          <i className="bi bi-tag" style={{ fontSize: '1.5rem', color: COLORS.info }}></i>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}> {/* Ajout de minWidth: 0 et flex: 1 */}
                          <h6 className="mb-1" style={{ 
                            color: COLORS.darkText, 
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            Type de produit
                          </h6>
                          <div className="mb-0" style={{ minWidth: 0 }}>
                            <span className="badge text-truncate" style={{ 
                              backgroundColor: `${COLORS.info}20`,
                              color: COLORS.info,
                              fontSize: '0.75rem',
                              padding: '0.375rem 0.75rem',
                              borderRadius: '4px',
                              fontWeight: 500,
                              display: 'inline-block',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {typeProduitLabel(selectedProduit.type_produit || selectedProduit.id_type_produit)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              <div className="col-md-3">
                <div className="card h-100" style={{ 
                  border: `1px solid ${selectedProduit.statut === 1 ? COLORS.success : COLORS.warning}`,
                  borderRadius: '12px',
                  boxShadow: `0 2px 8px rgba(${selectedProduit.statut === 1 ? '40, 167, 69' : '255, 193, 7'}, 0.1)`
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${selectedProduit.statut === 1 ? COLORS.success : COLORS.warning}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className={`bi ${selectedProduit.statut === 1 ? 'bi-check-circle' : 'bi-slash-circle'}`} 
                           style={{ fontSize: '1.5rem', color: selectedProduit.statut === 1 ? COLORS.success : COLORS.warning }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Statut
                        </h6>
                        <div className="mb-0">
                          <span className="badge" style={{ 
                            backgroundColor: selectedProduit.statut === 1 ? `${COLORS.success}20` : `${COLORS.warning}20`,
                            color: selectedProduit.statut === 1 ? COLORS.success : COLORS.warning,
                            fontSize: '0.75rem',
                            padding: '0.375rem 0.75rem',
                            borderRadius: '4px',
                            fontWeight: 500
                          }}>
                            {selectedProduit.statut === 1 ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3">
                <div className="card h-100" style={{ 
                  border: `1px solid ${COLORS.secondary}`,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(30, 136, 200, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.secondary}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-clock-history" style={{ fontSize: '1.5rem', color: COLORS.secondary }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Dernière modification
                        </h6>
                        <p className="mb-0 fw-bold" style={{ color: COLORS.secondary, fontSize: '0.875rem' }}>
                          {selectedProduit.updated_at && selectedProduit.updated_at !== 'null' && selectedProduit.updated_at !== '0000-00-00 00:00:00'
                            ? formatDateShort(selectedProduit.updated_at)
                            : "Non modifié"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Résumé des mouvements */}
            <div className="row mt-4">
              <div className="col-md-6">
                <div className="card h-100" style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(40, 167, 69, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.success}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-box-arrow-in-down" style={{ fontSize: '1.5rem', color: COLORS.success }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Total des entrées
                        </h6>
                        <h4 className="mb-0" style={{ color: COLORS.success, fontWeight: 700 }}>
                          {stats.totalEntrees} unités
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card h-100" style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(220, 53, 69, 0.1)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: `${COLORS.danger}15`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="bi bi-box-arrow-up" style={{ fontSize: '1.5rem', color: COLORS.danger }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ color: COLORS.darkText, fontSize: '0.875rem' }}>
                          Total des sorties
                        </h6>
                        <h4 className="mb-0" style={{ color: COLORS.danger, fontWeight: 700 }}>
                          {stats.totalSorties} unités
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-4">
                {/* Onglets pour entrées et sorties */}
                <div className="card" style={{ 
                  borderRadius: '12px',
                  border: `1px solid ${COLORS.lightBg}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <div className="card-body p-0">
                    <div className="border-bottom">
                      <ul className="nav nav-tabs border-0" id="detailTabs" role="tablist">
                        <li className="nav-item" role="presentation" style={{ marginBottom: '-1px' }}>
                          <button
                            className={`nav-link ${activeTab === 'entrees' ? 'active' : ''}`}
                            onClick={() => setActiveTab('entrees')}
                            type="button"
                            role="tab"
                            style={{
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: activeTab === 'entrees' ? COLORS.success : COLORS.darkText,
                              fontWeight: 500,
                              padding: '1rem 1.5rem',
                              borderRadius: '0',
                              borderBottom: activeTab === 'entrees' ? `3px solid ${COLORS.success}` : 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (activeTab !== 'entrees') {
                                e.target.style.color = COLORS.success;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (activeTab !== 'entrees') {
                                e.target.style.color = COLORS.darkText;
                              }
                            }}
                          >
                            <i className="bi bi-box-arrow-in-down me-2"></i>
                            Entrées de stock
                            <span className="badge ms-2" style={{ 
                              backgroundColor: `${COLORS.success}20`,
                              color: COLORS.success
                            }}>
                              {entreesProduit.length}
                            </span>
                          </button>
                        </li>
                        <li className="nav-item" role="presentation" style={{ marginBottom: '-1px' }}>
                          <button
                            className={`nav-link ${activeTab === 'sorties' ? 'active' : ''}`}
                            onClick={() => setActiveTab('sorties')}
                            type="button"
                            role="tab"
                            style={{
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: activeTab === 'sorties' ? COLORS.danger : COLORS.darkText,
                              fontWeight: 500,
                              padding: '1rem 1.5rem',
                              borderRadius: '0',
                              borderBottom: activeTab === 'sorties' ? `3px solid ${COLORS.danger}` : 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (activeTab !== 'sorties') {
                                e.target.style.color = COLORS.danger;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (activeTab !== 'sorties') {
                                e.target.style.color = COLORS.darkText;
                              }
                            }}
                          >
                            <i className="bi bi-box-arrow-up me-2"></i>
                            Sorties de stock
                            <span className="badge ms-2" style={{ 
                              backgroundColor: `${COLORS.danger}20`,
                              color: COLORS.danger
                            }}>
                              {sortiesProduit.length}
                            </span>
                          </button>
                        </li>
                      </ul>
                    </div>

                    <div className="p-4">
                      {/* Onglet Entrées */}
                      <div className={`${activeTab === 'entrees' ? '' : 'd-none'}`}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h5 style={{ color: COLORS.primary, fontWeight: 600, marginBottom: 0 }}>
                            <i className="bi bi-box-arrow-in-down text-success me-2"></i>
                            Historique des entrées de stock
                          </h5>
                          <button
                            onClick={handleEntrerStock}
                            className="btn"
                            style={{
                              color: COLORS.white,
                              backgroundColor: COLORS.success,
                              border: `1px solid ${COLORS.success}`,
                              fontWeight: 500,
                              borderRadius: '8px',
                              padding: '0.5rem 1rem'
                            }}
                          >
                            <i className="bi bi-plus-circle me-1"></i>
                            Entrer un stock
                          </button>
                        </div>
                        
                        {loadingEntrees ? (
                          <div className="text-center py-5">
                            <div className="spinner-border" role="status" style={{ color: COLORS.success }}>
                              <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des entrées de stock...</p>
                          </div>
                        ) : (
                          <>
                            {entreesProduit.length === 0 ? (
                              <div className="text-center py-5" style={{ backgroundColor: COLORS.lightBg, borderRadius: '8px' }}>
                                <div style={{
                                  width: '80px',
                                  height: '80px',
                                  backgroundColor: `${COLORS.success}15`,
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: '1rem'
                                }}>
                                  <i className="bi bi-inbox" style={{ fontSize: '2rem', color: COLORS.success }}></i>
                                </div>
                                <h5 className="mt-3" style={{ color: COLORS.primary }}>
                                  Aucune entrée de stock
                                </h5>
                                <p className="text-muted mb-3">
                                  Ce produit n'a encore reçu aucune entrée de stock.
                                </p>
                                <button
                                  onClick={handleEntrerStock}
                                  className="btn"
                                  style={{
                                    color: COLORS.white,
                                    backgroundColor: COLORS.success,
                                    border: `1px solid ${COLORS.success}`,
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    padding: '0.5rem 1.5rem'
                                  }}
                                >
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Effectuer la première entrée
                                </button>
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-hover align-middle" style={{ borderColor: COLORS.lightBg }}>
                                  <thead>
                                    <tr style={{ backgroundColor: COLORS.lightBg }}>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.success}`, padding: '1rem' }}>
                                        Date & Heure
                                      </th>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.success}`, padding: '1rem' }}>
                                        Quantité
                                      </th>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.success}`, padding: '1rem' }}>
                                        Statut
                                      </th>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.success}`, padding: '1rem' }}>
                                        Utilisateur
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entreesProduit.map((entree) => (
                                      <tr 
                                        key={entree.id_entree} 
                                        style={{ 
                                          borderBottom: `1px solid ${COLORS.lightBg}`,
                                          transition: 'background-color 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = `${COLORS.success}05`;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                      >
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <div className="d-flex flex-column">
                                            <span className="fw-medium" style={{ color: COLORS.darkText }}>
                                              {formatDateShort(entree.date_entree)}
                                            </span>
                                            <small className="text-muted">
                                              {new Date(entree.date_entree).toLocaleTimeString('fr-FR', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </small>
                                          </div>
                                        </td>
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <span className="badge" style={{ 
                                            backgroundColor: `${COLORS.success}20`,
                                            color: COLORS.success,
                                            padding: '0.375rem 0.75rem',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: 500
                                          }}>
                                            {entree.quantite_entree}
                                          </span>
                                        </td>
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <span className="badge" style={{ 
                                            backgroundColor: entree.statut_entree === 1 ? `${COLORS.success}20` : `${COLORS.secondary}20`,
                                            color: entree.statut_entree === 1 ? COLORS.success : COLORS.secondary,
                                            padding: '0.375rem 0.75rem',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: 500
                                          }}>
                                            {entree.statut_entree === 1 ? 'Actif' : 'Inactif'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <div className="d-flex flex-column">
                                            <span className="fw-medium" style={{ color: COLORS.darkText }}>
                                              {entree.utilisateur?.nom_complet || 'N/A'}
                                            </span>
                                            <small className="text-muted">
                                              {entree.utilisateur?.profile === 1 ? 'Administrateur' : 
                                              entree.utilisateur?.profile === 2 ? 'Contrôleur' : 
                                              entree.utilisateur?.profile === 3 ? 'Gestionnaire' : 'Utilisateur'}
                                            </small>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Onglet Sorties */}
                      <div className={`${activeTab === 'sorties' ? '' : 'd-none'}`}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h5 style={{ color: COLORS.primary, fontWeight: 600, marginBottom: 0 }}>
                            <i className="bi bi-box-arrow-up text-danger me-2"></i>
                            Historique des sorties de stock
                          </h5>
                          <button
                            onClick={handleSortirStock}
                            className="btn"
                            style={{
                              color: COLORS.white,
                              backgroundColor: COLORS.danger,
                              border: `1px solid ${COLORS.danger}`,
                              fontWeight: 500,
                              borderRadius: '8px',
                              padding: '0.5rem 1rem'
                            }}
                          >
                            <i className="bi bi-dash-circle me-1"></i>
                            Sortir du stock
                          </button>
                        </div>
                        
                        {loadingSorties ? (
                          <div className="text-center py-5">
                            <div className="spinner-border" role="status" style={{ color: COLORS.danger }}>
                              <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des sorties de stock...</p>
                          </div>
                        ) : (
                          <>
                            {sortiesProduit.length === 0 ? (
                              <div className="text-center py-5" style={{ backgroundColor: COLORS.lightBg, borderRadius: '8px' }}>
                                <div style={{
                                  width: '80px',
                                  height: '80px',
                                  backgroundColor: `${COLORS.danger}15`,
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: '1rem'
                                }}>
                                  <i className="bi bi-box-arrow-up" style={{ fontSize: '2rem', color: COLORS.danger }}></i>
                                </div>
                                <h5 className="mt-3" style={{ color: COLORS.primary }}>
                                  Aucune sortie de stock
                                </h5>
                                <p className="text-muted mb-3">
                                  Aucune sortie n'a encore été effectuée pour ce produit.
                                </p>
                                <button
                                  onClick={handleSortirStock}
                                  className="btn"
                                  style={{
                                    color: COLORS.white,
                                    backgroundColor: COLORS.danger,
                                    border: `1px solid ${COLORS.danger}`,
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    padding: '0.5rem 1.5rem'
                                  }}
                                >
                                  <i className="bi bi-dash-circle me-1"></i>
                                  Effectuer la première sortie
                                </button>
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-hover align-middle" style={{ borderColor: COLORS.lightBg }}>
                                  <thead>
                                    <tr style={{ backgroundColor: COLORS.lightBg }}>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.danger}`, padding: '1rem' }}>
                                        Date & Heure
                                      </th>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.danger}`, padding: '1rem' }}>
                                        Quantité
                                      </th>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.danger}`, padding: '1rem' }}>
                                        Motif
                                      </th>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.danger}`, padding: '1rem' }}>
                                        Destination
                                      </th>
                                      <th style={{ color: COLORS.darkText, fontWeight: 600, borderBottom: `2px solid ${COLORS.danger}`, padding: '1rem' }}>
                                        Utilisateur
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sortiesProduit.map((sortie) => (
                                      <tr 
                                        key={sortie.id_sortie || sortie.id_sortie_stock} 
                                        style={{ 
                                          borderBottom: `1px solid ${COLORS.lightBg}`,
                                          transition: 'background-color 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = `${COLORS.danger}05`;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                      >
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <div className="d-flex flex-column">
                                            <span className="fw-medium" style={{ color: COLORS.darkText }}>
                                              {formatDateShort(sortie.date_sortie || sortie.created_at)}
                                            </span>
                                            <small className="text-muted">
                                              {new Date(sortie.date_sortie || sortie.created_at).toLocaleTimeString('fr-FR', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </small>
                                          </div>
                                        </td>
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <span className="badge" style={{ 
                                            backgroundColor: `${COLORS.danger}20`,
                                            color: COLORS.danger,
                                            padding: '0.375rem 0.75rem',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: 500
                                          }}>
                                            {sortie.quantite_sortie || sortie.quantite_sortie_stock}
                                          </span>
                                        </td>
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <div className="text-truncate" style={{ maxWidth: '200px' }} 
                                              title={sortie.motif || sortie.motif_sortie_stock}>
                                            {sortie.motif || sortie.motif_sortie_stock}
                                          </div>
                                        </td>
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <div className="d-flex flex-column">
                                            <span className="fw-medium" style={{ color: COLORS.darkText }}>
                                              {getDestinationLabel(sortie.destination)}
                                            </span>
                                            {sortie.incident && (
                                              <small className="text-muted">
                                                Incident: {sortie.incident.numero_ticket || 'N/A'}
                                              </small>
                                            )}
                                          </div>
                                        </td>
                                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                                          <div className="d-flex flex-column">
                                            <span className="fw-medium" style={{ color: COLORS.darkText }}>
                                              {sortie.utilisateur?.nom_complet || 
                                              (sortie.utilisateur?.nom && sortie.utilisateur?.prenom ? 
                                                `${sortie.utilisateur.nom} ${sortie.utilisateur.prenom}` : 
                                                'N/A')}
                                            </span>
                                            <small className="text-muted">
                                              {sortie.utilisateur?.profile === 1 ? 'Administrateur' : 
                                              sortie.utilisateur?.profile === 2 ? 'Contrôleur' : 
                                              sortie.utilisateur?.profile === 3 ? 'Gestionnaire' : 
                                              sortie.utilisateur?.profile === 4 ? 'Agent' : 'Utilisateur'}
                                            </small>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
            </div>

          </div>
        </div>
      </section>

      <style jsx="true">{`
        .nav-tabs .nav-link {
          border: 1px solid transparent;
          border-top-left-radius: 0.25rem;
          border-top-right-radius: 0.25rem;
        }
        .nav-tabs .nav-link.active {
          color: #495057;
          background-color: #fff;
          border-color: #dee2e6 #dee2e6 #fff;
        }
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          cursor: pointer;
        }
      `}</style>
    </Layout>
  );
};

export default ProductDetail;